import { useMemo } from 'react'
import type { Category } from '@zeni/shared'
import { useLanguage } from '../contexts/LanguageContext'
import { SearchableSelect, type SelectOption } from './SearchableSelect'

interface Props {
  categories: Category[]
  value: string
  onChange: (value: string) => void
  /** 未選択を選べるようにする場合の表示ラベル（「全カテゴリ」「変更しない」など）。省略すると空の選択肢を出さない */
  emptyLabel?: string
  /** 値も emptyLabel も無いときにトリガーへ出す文言 */
  placeholder?: string
  /** トリガーに適用するクラス。置き換え前の select と同じものを渡す */
  className?: string
  disabled?: boolean
  'data-testid'?: string
}

/**
 * カテゴリ選択用のコンボボックス。
 * 中身は汎用の {@link SearchableSelect} で、カテゴリの絵文字と文言だけを受け持つ。
 */
export function CategoryCombobox({
  categories,
  value,
  onChange,
  emptyLabel,
  placeholder,
  className,
  disabled,
  'data-testid': testId,
}: Props) {
  const { t } = useLanguage()

  const options = useMemo<SelectOption[]>(() => {
    const head: SelectOption[] = emptyLabel !== undefined ? [{ value: '', label: emptyLabel }] : []
    return [...head, ...categories.map((c) => ({ value: c.id, label: c.name, icon: c.icon }))]
  }, [categories, emptyLabel])

  return (
    <SearchableSelect
      options={options}
      value={value}
      onChange={onChange}
      placeholder={placeholder ?? t('category_search_placeholder')}
      searchPlaceholder={t('category_search_placeholder')}
      className={className}
      disabled={disabled}
      data-testid={testId}
    />
  )
}
